// Post-signup onboarding page. After a company is created (no free trial
// anymore) we show the owner an editorial-feeling walkthrough of what Rostr
// does. The single primary CTA takes them to /billing to actually pick a plan.
// A quiet secondary link lets them peek at the empty dashboard first.
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  CalendarDays, ClipboardList, Repeat, Shield, Bell, Users,
  BarChart3, MessagesSquare, FileCheck2, Crown, ArrowRight, CheckCircle2,
} from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import api from "@/lib/api";

function money(amount, currency) {
  if (amount == null) return "—";
  return new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: (currency || "eur").toUpperCase(),
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount / 100);
}

const FEATURES = (t) => [
  { Icon: CalendarDays, title: t("welcome_f1_title"), desc: t("welcome_f1_desc") },
  { Icon: ClipboardList, title: t("welcome_f2_title"), desc: t("welcome_f2_desc") },
  { Icon: Repeat, title: t("welcome_f3_title"), desc: t("welcome_f3_desc") },
  { Icon: Users, title: t("welcome_f4_title"), desc: t("welcome_f4_desc") },
  { Icon: BarChart3, title: t("welcome_f5_title"), desc: t("welcome_f5_desc") },
  { Icon: Shield, title: t("welcome_f6_title"), desc: t("welcome_f6_desc") },
  { Icon: Bell, title: t("welcome_f7_title"), desc: t("welcome_f7_desc") },
  { Icon: FileCheck2, title: t("welcome_f8_title"), desc: t("welcome_f8_desc") },
  { Icon: MessagesSquare, title: t("welcome_f9_title"), desc: t("welcome_f9_desc") },
];

export default function Welcome() {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const firstName = (user?.first_name || user?.name || "").split(" ")[0];
  const [plans, setPlans] = useState([]);
  const [checkingOut, setCheckingOut] = useState(null);

  useEffect(() => {
    api.get("/billing/plans")
      .then(({ data }) => setPlans(data.plans || []))
      .catch(() => {});
  }, []);

  const startCheckout = async (lookup_key) => {
    setCheckingOut(lookup_key);
    try {
      const { data } = await api.post("/billing/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch {
      // fall through — button re-enables, user can retry or click Later
      setCheckingOut(null);
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--bg))] relative overflow-hidden">
      <div className="hero-mesh" aria-hidden />
      <div
        className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full pointer-events-none blur-3xl opacity-40"
        aria-hidden
        style={{ background: "radial-gradient(circle, hsl(38 92% 60% / 0.35), transparent 65%)" }}
      />

      <nav className="relative z-10 max-w-5xl mx-auto flex items-center justify-between px-6 lg:px-10 py-6">
        <Link to="/dashboard" className="flex items-center gap-2.5">
          <span className="font-display text-[20px] font-bold tracking-tight text-[hsl(var(--text-strong))]">Rostr</span>
        </Link>
        <div className="chip chip-primary text-[11px]" data-testid="welcome-owner-chip">
          <Crown size={11} strokeWidth={2.2} /> {t("role_owner")}
        </div>
      </nav>

      <section className="relative z-10 max-w-5xl mx-auto px-6 lg:px-10 pt-6 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[hsl(var(--primary))] mb-5"
        >
          {t("welcome_eyebrow")}
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
          className="font-display text-[38px] md:text-[52px] leading-[1.02] tracking-[-0.02em] text-[hsl(var(--text-strong))] max-w-2xl"
          data-testid="welcome-title"
        >
          {firstName ? t("welcome_title_named").replace("{name}", firstName) : t("welcome_title_plain")}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
          className="mt-5 text-[16px] leading-[1.65] text-[hsl(var(--text-muted))] max-w-2xl"
        >
          {t("welcome_intro_p1")}
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.22 }}
          className="mt-3 text-[15px] leading-[1.65] text-[hsl(var(--text-muted))] max-w-2xl"
        >
          {t("welcome_intro_p2")}
        </motion.p>

        {/* Feature grid — editorial 3-col layout */}
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES(t).map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + i * 0.04, duration: 0.4 }}
              className="card p-5 md:p-6"
              data-testid={`welcome-feature-${i + 1}`}
            >
              <div className="w-10 h-10 rounded-xl bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))] grid place-items-center mb-3.5">
                <f.Icon size={17} strokeWidth={1.75} />
              </div>
              <div className="font-display text-[16px] font-bold tracking-tight text-[hsl(var(--text-strong))]">{f.title}</div>
              <div className="text-[13px] text-[hsl(var(--text-muted))] mt-1.5 leading-relaxed">{f.desc}</div>
            </motion.div>
          ))}
        </div>

        {/* Plan-preview grid — 4 compact plan cards + Custom row */}
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.5 }}
          className="mt-14"
          data-testid="welcome-plans"
        >
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[hsl(var(--primary))] mb-2">
            {t("welcome_decide_eyebrow")}
          </div>
          <h2 className="font-display text-[26px] md:text-[30px] leading-[1.05] tracking-tight text-[hsl(var(--text-strong))]">
            {t("welcome_decide_title")}
          </h2>
          <p className="mt-3 text-[14.5px] leading-relaxed text-[hsl(var(--text-muted))] max-w-2xl">
            {t("welcome_decide_body")}
          </p>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {plans.map((p) => {
              const range = p.min_employees && p.max_employees
                ? `${p.min_employees}–${p.max_employees} ${t("size_employees")}`
                : "";
              const recommended = p.lookup_key === "team_monthly";
              return (
                <div
                  key={p.lookup_key}
                  className={`card p-5 relative ${recommended ? "ring-2 ring-[hsl(var(--primary))]" : ""}`}
                  data-testid={`welcome-plan-${p.lookup_key}`}
                >
                  {recommended && (
                    <div className="absolute -top-2.5 left-4 chip chip-primary !text-[10px]">
                      {t("billing_recommended")}
                    </div>
                  )}
                  <div className="font-display text-[17px] font-bold tracking-tight text-[hsl(var(--text-strong))]">{p.name}</div>
                  <div className="text-[11px] text-[hsl(var(--text-muted))] mt-0.5 tabular-nums">{range}</div>
                  <div className="text-[10.5px] font-semibold text-[hsl(var(--primary))] mt-1 leading-snug">
                    {t(`plan_best_for_${p.lookup_key}`)}
                  </div>
                  <div className="mt-3 flex items-baseline gap-1">
                    <div className="font-display text-[24px] leading-none font-bold tabular-nums text-[hsl(var(--text-strong))]">
                      {money(p.amount, p.currency)}
                    </div>
                    <div className="text-[11px] text-[hsl(var(--text-muted))]">/ {t("billing_per_month")}</div>
                  </div>
                  <button
                    onClick={() => startCheckout(p.lookup_key)}
                    disabled={checkingOut === p.lookup_key}
                    data-testid={`welcome-checkout-${p.lookup_key}`}
                    className="btn btn-primary w-full mt-4 !h-9 !text-[12px]"
                  >
                    {checkingOut === p.lookup_key ? t("working") : t("welcome_pick_now")}
                    <ArrowRight size={12} strokeWidth={2.2} />
                  </button>
                </div>
              );
            })}
          </div>

          {/* Decision-line: "Ja alle plans" OR "Niet nu" */}
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button
              onClick={() => navigate("/billing")}
              data-testid="welcome-continue-cta"
              className="btn btn-primary !h-12 !px-6 !text-[14.5px] group"
            >
              {t("welcome_cta_yes")}
              <ArrowRight size={16} strokeWidth={2.2} className="transition-transform group-hover:translate-x-0.5" />
            </button>
            <button
              onClick={() => navigate("/dashboard")}
              data-testid="welcome-later-link"
              className="text-[13.5px] font-semibold text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))] underline underline-offset-[6px] decoration-[hsl(var(--border-strong))] decoration-[1.5px] hover:decoration-[hsl(var(--primary))] px-2 h-12 inline-flex items-center"
              title={t("welcome_later_hint")}
            >
              {t("welcome_cta_later")}
            </button>
          </div>

          {/* Warning: "Niet nu = niks werkt" */}
          <div className="mt-4 flex items-start gap-2 text-[12.5px] text-[hsl(var(--text-muted))] max-w-2xl">
            <CheckCircle2 size={13} strokeWidth={2} className="mt-0.5 text-[hsl(var(--warning))]" />
            <span>{t("welcome_later_hint")}</span>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
